import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import type {
  AddRoleRequirementInput,
  TosRoleRequirement,
  TosRoleScope,
} from "../shared/compliance-types";

export const REQUIREMENTS_QUERY_KEY = ["compliance", "requirements"] as const;

async function fetchRequirements(): Promise<readonly TosRoleRequirement[]> {
  return httpClient<readonly TosRoleRequirement[]>("/api/admin/tos/role-requirements");
}

export function useRequirements() {
  return useQuery({
    queryKey: REQUIREMENTS_QUERY_KEY,
    queryFn: fetchRequirements,
    staleTime: 60_000,
  });
}

async function addRequirement(input: AddRoleRequirementInput): Promise<TosRoleRequirement> {
  return httpClient<TosRoleRequirement>("/api/admin/tos/role-requirements", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
}

export function useAddRequirement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addRequirement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEY });
    },
  });
}

interface RemoveRequirementParams {
  readonly role: TosRoleScope;
  readonly documentTypeId: string;
}

async function removeRequirement({ role, documentTypeId }: RemoveRequirementParams): Promise<void> {
  await httpClient<void>(
    `/api/admin/tos/role-requirements/${encodeURIComponent(role)}/${encodeURIComponent(documentTypeId)}`,
    { method: "DELETE" },
  );
}

export function useRemoveRequirement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeRequirement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REQUIREMENTS_QUERY_KEY });
    },
  });
}
